import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePeriodo } from '../../contexts/PeriodoContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, Download, Loader2, Users, Search, CheckSquare, Square, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { formatarDataBR, formatarPeriodo } from '../../utils/dateUtils';
import { InputDataBR } from '../../components/ui/input-data-br';

const CartaoPontoPage = () => {
  const { api, user } = useAuth();
  const { periodoSelecionado } = usePeriodo();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [empregados, setEmpregados] = useState([]);
  const [empregadosSelecionados, setEmpregadosSelecionados] = useState([]);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [filtroNome, setFiltroNome] = useState('');

  // Sincronizar com período global
  useEffect(() => {
    if (periodoSelecionado?.data_inicio && periodoSelecionado?.data_fim) {
      setDataInicio(periodoSelecionado.data_inicio);
      setDataFim(periodoSelecionado.data_fim);
    }
  }, [periodoSelecionado]);

  useEffect(() => {
    fetchEmpregados();
  }, []);

  const fetchEmpregados = async () => {
    setLoading(true);
    try {
      const response = await api.get('/empregados');
      // Filtrar apenas ativos
      const ativos = response.data.filter(e => e.ativo !== false);
      setEmpregados(ativos);
    } catch (error) {
      toast.error('Erro ao carregar empregados');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelecionarTodos = () => {
    const empregadosFiltrados = empregados.filter(e => 
      e.nome.toLowerCase().includes(filtroNome.toLowerCase()) ||
      (e.matricula && e.matricula.toLowerCase().includes(filtroNome.toLowerCase()))
    );
    
    if (empregadosSelecionados.length === empregadosFiltrados.length) {
      setEmpregadosSelecionados([]);
    } else {
      setEmpregadosSelecionados(empregadosFiltrados.map(e => e.id));
    }
  };

  const toggleSelecionarEmpregado = (empregadoId) => {
    if (empregadosSelecionados.includes(empregadoId)) {
      setEmpregadosSelecionados(empregadosSelecionados.filter(id => id !== empregadoId));
    } else {
      setEmpregadosSelecionados([...empregadosSelecionados, empregadoId]);
    }
  };

  const gerarCartaoPonto = async () => {
    if (empregadosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um empregado');
      return;
    }

    if (!dataInicio || !dataFim) {
      toast.error('Selecione o período');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/cartao-ponto/gerar', {
        empregado_ids: empregadosSelecionados,
        data_inicio: dataInicio,
        data_fim: dataFim
      }, {
        responseType: 'blob'
      });

      // Criar URL para download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nome do arquivo
      const nomeArquivo = empregadosSelecionados.length === 1
        ? `cartao_ponto_${empregados.find(e => e.id === empregadosSelecionados[0])?.nome?.replace(/\s/g, '_') || 'empregado'}.pdf`
        : `cartoes_ponto_${dataInicio}_${dataFim}.pdf`;
      
      link.download = nomeArquivo;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Cartão de ponto gerado para ${empregadosSelecionados.length} empregado(s)!`);
    } catch (error) {
      console.error('Erro ao gerar cartão:', error);
      toast.error('Erro ao gerar cartão de ponto');
    } finally {
      setGenerating(false);
    }
  };

  const empregadosFiltrados = empregados.filter(e => 
    e.nome.toLowerCase().includes(filtroNome.toLowerCase()) ||
    (e.matricula && e.matricula.toLowerCase().includes(filtroNome.toLowerCase()))
  );

  const todosSelecionados = empregadosFiltrados.length > 0 && 
    empregadosFiltrados.every(e => empregadosSelecionados.includes(e.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cartao-ponto-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-1">Cartão de Ponto</h2>
          <p className="text-slate-500">Gere cartões de ponto preenchidos com dados das jornadas</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Período e Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end flex-wrap">
            <div>
              <Label>Data Início</Label>
              <InputDataBR
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="w-36"
                data-testid="input-data-inicio"
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <InputDataBR
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="w-36"
                data-testid="input-data-fim"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label>Buscar Empregado</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Nome ou matrícula..."
                  value={filtroNome}
                  onChange={(e) => setFiltroNome(e.target.value)}
                  className="pl-10"
                  data-testid="input-filtro-nome"
                />
              </div>
            </div>
            <Button 
              onClick={gerarCartaoPonto}
              disabled={generating || empregadosSelecionados.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-gerar-cartao"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Gerar PDF ({empregadosSelecionados.length})
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Empregados */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4" />
                Selecione os Empregados
              </CardTitle>
              <CardDescription>
                {empregadosSelecionados.length} de {empregadosFiltrados.length} selecionado(s)
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSelecionarTodos}
              data-testid="btn-selecionar-todos"
            >
              {todosSelecionados ? (
                <>
                  <Square className="w-4 h-4 mr-2" />
                  Desmarcar Todos
                </>
              ) : (
                <>
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Selecionar Todos
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Matrícula</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Setor</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {empregadosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      Nenhum empregado encontrado
                    </TableCell>
                  </TableRow>
                ) : (
                  empregadosFiltrados.map((empregado) => (
                    <TableRow 
                      key={empregado.id}
                      className={`cursor-pointer hover:bg-slate-50 ${
                        empregadosSelecionados.includes(empregado.id) ? 'bg-emerald-50' : ''
                      }`}
                      onClick={() => toggleSelecionarEmpregado(empregado.id)}
                    >
                      <TableCell>
                        <Checkbox
                          checked={empregadosSelecionados.includes(empregado.id)}
                          onCheckedChange={() => toggleSelecionarEmpregado(empregado.id)}
                          data-testid={`checkbox-${empregado.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono">{empregado.matricula || '-'}</TableCell>
                      <TableCell className="font-medium">{empregado.nome}</TableCell>
                      <TableCell>{empregado.cargo || '-'}</TableCell>
                      <TableCell>{empregado.setor || empregado.departamento || '-'}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant={empregado.ativo !== false ? 'default' : 'secondary'}>
                          {empregado.ativo !== false ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Sobre o Cartão de Ponto</p>
              <ul className="list-disc list-inside space-y-1">
                <li>O cartão é gerado com dados reais das marcações e jornadas processadas</li>
                <li>Inclui colunas de HE separadas por percentual conforme suas faixas configuradas</li>
                <li>Verbas com evento de folha aparecem na seção de totalização</li>
                <li>Selecione múltiplos empregados para gerar um PDF combinado</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CartaoPontoPage;
